import { Banner, Paragraph, Stack } from '@customerio/pluma-components/react';
import plur from 'plur';
import { useRouter } from '@tanstack/react-router';
import { useProductTaxonomy } from '@/services/product-taxonomy';
import { isGeoIpAttribute } from '../utils/item';

/**
 * What a bulk delete is about to do.
 *
 * Some attributes Customer.io writes from a visitor's IP address are written
 * again the next time the workspace updates a person, so deleting them is
 * undone by the next thing that happens. The workspace can stop that, but not
 * from here — hence the way out to the setting.
 *
 * The wording is the page this replaces
 * (`app/components/bulk-delete-confirmation-modal/index.gts:73-90`), including
 * the workspace's own noun for its people, which the banner needs to say what
 * regenerates the data.
 */

export type DeleteMessageProps = {
  workspaceId: number;
  names: readonly string[];
};

export function DeleteMessage({ workspaceId, names }: DeleteMessageProps) {
  const router = useRouter();
  const taxonomy = useProductTaxonomy();
  const generated = names.filter(isGeoIpAttribute);
  const settingsHref = router.buildLocation({
    to: '/workspaces/$workspaceId/settings/actions/time_zone_match',
    params: { workspaceId: String(workspaceId) },
  }).href;

  return (
    <Stack gap="200">
      {generated.length > 0 ? (
        <Banner
          variant="caution"
          title="Deleting auto-generated geolocation attributes"
          description={`${generated.length} of the attributes you selected are automatically generated by Customer.io. If you've enabled geolocation, Customer.io will regenerate this data when you update ${taxonomy.term('people.plural')}.`}
          action={{ label: 'Go to settings', href: settingsHref, variant: 'primary' }}
        />
      ) : null}
      <Paragraph>
        Are you sure you want to delete the selected {plur('attribute', names.length)}? This action is permanent and
        cannot be undone.
      </Paragraph>
    </Stack>
  );
}
